import React, { useEffect, useState } from 'react';
import { Layers, LayoutGrid, ClipboardList, Calendar, Send } from 'lucide-react';
import { mccGet, mccPatch } from './mccApi';
import { toast } from 'sonner';
import { SectionHeader, StatCard, EmptyState, PlatformIcon } from './mccUi';
import { PLATFORMS, platformMeta } from './mccConstants';
import MccContentCard from './MccContentCard';
import MccKanban from './MccKanban';
import MccProjectList from './MccProjectList';
import { PROJECT_STAGES } from './mccConstants';

export default function MccAllPlatforms({ team, onOpenProject }) {
  const initial = localStorage.getItem('mcc-last-platform') || 'all';
  const [platform, setPlatform] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('summary');
  const nameOf = (uid) => (team.find((t) => t.id === uid) || {}).name || uid;

  const load = async (p) => {
    setLoading(true);
    try {
      const res = await mccGet(`/marketing/platforms/${p}`);
      setData(res);
    } catch (e) { toast.error(e.message || 'Failed to load platform'); }
    finally { setLoading(false); }
  };
  useEffect(() => { localStorage.setItem('mcc-last-platform', platform); load(platform); }, [platform]);

  const moveProject = async (item, newStatus) => {
    try { await mccPatch(`/marketing/projects/${item.id}`, { status: newStatus }); await load(platform); toast.success('Moved'); }
    catch (e) { toast.error(e.message || 'Move failed'); }
  };

  const meta = platform === 'all' ? { l: 'All Platforms' } : platformMeta(platform);
  const bg = platform === 'all' ? '#4f46e5' : (meta?.tone || '#4f46e5');

  return (
    <div className="p-6 space-y-4">
      <SectionHeader
        title="All Platforms"
        subtitle="See activity across every marketing platform — or drill into one."
        icon={Layers}
      />

      {/* Platform picker */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3">
        <button onClick={() => setPlatform('all')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
            platform === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
          }`}
          data-testid="mcc-platform-tab-all"
        >All Platforms</button>
        {PLATFORMS.map((p) => {
          const Icon = p.icon;
          const active = platform === p.v;
          return (
            <button key={p.v} onClick={() => setPlatform(p.v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                active ? 'text-white border-transparent' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
              style={{ background: active ? p.tone : undefined }}
              data-testid={`mcc-platform-tab-${p.v}`}
            >
              <Icon className="w-3.5 h-3.5" style={{ color: active ? '#fff' : p.tone }} /> {p.l}
            </button>
          );
        })}
      </div>

      {/* Hero */}
      <div className="rounded-2xl overflow-hidden text-white p-6" style={{ background: platform === 'all' ? 'linear-gradient(135deg,#4f46e5,#4338ca)' : (platformMeta(platform)?.bg || '#4f46e5') }}>
        <div className="flex items-center gap-3">
          {platform !== 'all' ? <PlatformIcon platform={platform} size={44} /> : <Layers className="w-10 h-10" />}
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-80">Platform view</p>
            <h2 className="text-2xl font-bold">{meta?.l || 'All Platforms'}</h2>
            <p className="text-sm opacity-80">All content activity for this platform in one visual view.</p>
          </div>
        </div>
      </div>

      {loading || !data ? (
        <p className="text-sm text-slate-500 p-8 text-center">Loading…</p>
      ) : (
        <>
          {/* Counters */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <StatCard label="Active" value={data.counts.active} tone="indigo" icon={LayoutGrid} />
            <StatCard label="Awaiting approval" value={data.counts.awaiting_approval} tone="amber" />
            <StatCard label="Changes required" value={data.counts.changes_required} tone="rose" />
            <StatCard label="Ready to publish" value={data.counts.ready_to_publish} tone="emerald" />
            <StatCard label="Scheduled" value={data.counts.scheduled} tone="violet" icon={Calendar} />
            <StatCard label="Published" value={data.counts.published} tone="emerald" icon={Send} />
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5 w-fit">
            {['summary', 'kanban', 'list', 'published'].map((v) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1 text-xs font-semibold rounded transition ${view === v ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'}`}
              >{v === 'summary' ? 'Summary' : v === 'kanban' ? 'Kanban' : v === 'list' ? 'List' : 'Published'}</button>
            ))}
          </div>

          {view === 'summary' && (
            <div className="grid md:grid-cols-2 gap-4">
              <BucketList title="Awaiting approval" items={data.awaiting_approval} nameOf={nameOf} onOpenProject={onOpenProject} />
              <BucketList title="Changes required" items={data.changes_required} nameOf={nameOf} onOpenProject={onOpenProject} />
              <BucketList title="Ready to publish" items={data.ready_to_publish} nameOf={nameOf} onOpenProject={onOpenProject} />
              <BucketList title="Scheduled" items={data.scheduled} nameOf={nameOf} onOpenProject={onOpenProject} />
            </div>
          )}
          {view === 'kanban' && (
            <MccKanban stages={PROJECT_STAGES} items={data.active} variant="project" nameOf={nameOf}
              onOpen={(p) => onOpenProject(p.id)}
              onMove={moveProject} />
          )}
          {view === 'list' && (
            <MccProjectList items={data.active} nameOf={nameOf} onOpenProject={onOpenProject} groupByStatus />
          )}
          {view === 'published' && (
            data.published.length === 0 ? (
              <EmptyState title="Nothing published yet on this platform" body="Once you publish, entries will appear here." />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.published.map((p) => <MccContentCard key={p.id} item={p} nameOf={nameOf} onClick={() => onOpenProject(p.id)} />)}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}

function BucketList({ title, items, nameOf, onOpenProject }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-sm font-bold text-slate-800">{title}</p>
        <span className="text-[10px] font-bold text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-500 py-4 text-center">Empty</p>
      ) : (
        <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
          {items.map((p) => <MccContentCard key={p.id} item={p} nameOf={nameOf} onClick={() => onOpenProject(p.id)} />)}
        </div>
      )}
    </div>
  );
}
